import React, { useEffect, useRef, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Check, X, FileQuestion, Loader2, MapPin, User, CalendarClock, Euro,
  Upload, CheckCircle2, Wrench, Trash2, Play, Camera,
} from 'lucide-react';
import { facilityAPI } from '../../lib/api';
import { PhotoThumb } from './PhotoThumb';
import {
  CATEGORY_LABELS, URGENCY_LABELS, URGENCY_STYLES, STATUS_LABELS, STATUS_STYLES,
  ORDER_TYPE_LABELS, TYPE_FIELDS, RECURRENCE_LABELS, fmtDateTimeDe,
} from './constants';
import { toast } from 'sonner';

const pad2 = (n) => String(n).padStart(2, '0');
const timeOf = (iso) => { if (!iso) return '09:00'; const d = new Date(iso); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const dateOf = (iso) => { if (!iso) return ''; const d = new Date(iso); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

export const TicketDetailSheet = ({ ticketId, open, onOpenChange, meta, technicians = [], onChanged }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decisionNote, setDecisionNote] = useState('');
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNote, setQuoteNote] = useState('');
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('09:00');
  const [schedEndDate, setSchedEndDate] = useState('');
  const [schedEndTime, setSchedEndTime] = useState('');
  const [completeNote, setCompleteNote] = useState('');
  const [materialCost, setMaterialCost] = useState('');
  const [workMinutes, setWorkMinutes] = useState('');
  const quoteFileRef = useRef(null);
  const quoteCamRef = useRef(null);
  const afterFileRef = useRef(null);
  const afterCamRef = useRef(null);

  const canManage = meta?.can_manage;
  const canWork = meta?.can_work;

  const reload = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await facilityAPI.get(ticketId);
      setTicket(res.data);
      setQuoteAmount(res.data.quote?.amount != null ? String(res.data.quote.amount) : '');
      setQuoteNote(res.data.quote?.note || '');
      setSchedDate(dateOf(res.data.scheduled_start));
      setSchedTime(timeOf(res.data.scheduled_start));
      setSchedEndDate(dateOf(res.data.scheduled_end));
      setSchedEndTime(res.data.scheduled_end ? timeOf(res.data.scheduled_end) : '');
    } catch {
      toast.error('Meldung konnte nicht geladen werden');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && ticketId) { reload(); setDecisionNote(''); } }, [open, ticketId]); // eslint-disable-line

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await facilityAPI.remove(ticketId);
      toast.success('Meldung entfernt');
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const photos = ticket?.photos || [];
  const reportPhotos = photos.filter((p) => p.kind === 'report');
  const quotePhotos = photos.filter((p) => p.kind === 'quote');
  const completionPhotos = photos.filter((p) => p.kind === 'completion');

  const uploadKind = (files, kind) => act(async () => {
    for (const f of Array.from(files || [])) {
      await facilityAPI.uploadPhoto(ticketId, f, kind);
    }
  }, 'Datei hochgeladen');

  const saveSchedule = () => {
    if (!schedDate) { toast.error('Bitte ein Datum wählen'); return; }
    const startIso = new Date(`${schedDate}T${schedTime || '09:00'}:00`).toISOString();
    let endIso = null;
    if (schedEndDate) {
      endIso = new Date(`${schedEndDate}T${schedEndTime || schedTime || '17:00'}:00`).toISOString();
      if (endIso < startIso) { toast.error('Ende darf nicht vor dem Beginn liegen'); return; }
    }
    act(() => facilityAPI.schedule(ticketId, { scheduled_start: startIso, scheduled_end: endIso }), 'Termin gesetzt');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="facility-detail-sheet">
        {loading || !ticket ? (
          <>
            <SheetHeader className="sr-only"><SheetTitle>Meldung</SheetTitle></SheetHeader>
            <div className="flex items-center justify-center h-40"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          </>
        ) : (
          <>
            <SheetHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={STATUS_STYLES[ticket.status]} data-testid="facility-detail-status">{STATUS_LABELS[ticket.status]}</Badge>
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">{ORDER_TYPE_LABELS[ticket.order_type] || 'Auftrag'}</Badge>
                <Badge variant="outline" className={URGENCY_STYLES[ticket.urgency]}>{URGENCY_LABELS[ticket.urgency]}</Badge>
                {ticket.order_type === 'schaden' && <Badge variant="outline">{CATEGORY_LABELS[ticket.category] || ticket.category}</Badge>}
                {ticket.source === 'employee' && <Badge variant="outline" className="text-amber-700 border-amber-300">Mitarbeiter-Meldung</Badge>}
              </div>
              <SheetTitle className="font-heading text-left">{ticket.title}</SheetTitle>
              <SheetDescription className="text-left space-y-1">
                {ticket.description && <span className="block text-foreground/80">{ticket.description}</span>}
                <span className="flex items-center gap-3 text-xs flex-wrap pt-1">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{ticket.location_name || 'Kein Standort'}{ticket.room_name ? ` · ${ticket.room_name}` : ''}</span>
                  <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{ticket.reporter_name || '–'}</span>
                </span>
              </SheetDescription>
            </SheetHeader>

            <div className="space-y-5 py-4">
              {/* Typ-spezifische Angaben */}
              {(TYPE_FIELDS[ticket.order_type] || []).some((f) => ticket.details?.[f.key]) && (
                <div className="rounded-lg border border-border/50 p-3 bg-secondary/20 text-sm space-y-1" data-testid="facility-detail-details">
                  {(TYPE_FIELDS[ticket.order_type] || []).filter((f) => ticket.details?.[f.key]).map((f) => (
                    <div key={f.key} className="flex gap-2">
                      <span className="text-muted-foreground min-w-[110px]">{f.label}:</span>
                      <span className="font-medium">{f.key === 'recurrence' ? (RECURRENCE_LABELS[ticket.details[f.key]] || ticket.details[f.key]) : ticket.details[f.key]}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fotos der Meldung */}
              {reportPhotos.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Fotos zur Meldung</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {reportPhotos.map((p) => <PhotoThumb key={p.id} photo={p} />)}
                  </div>
                </div>
              )}

              {/* Leitung: Freigabe-Workflow */}
              {canManage && ['reported', 'quote_requested'].includes(ticket.status) && (
                <div className="rounded-xl border border-border/60 p-3 space-y-2 bg-secondary/20">
                  <Label className="text-sm font-medium">Entscheidung der Leitung</Label>
                  <Textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder="Notiz (optional)" rows={2} data-testid="facility-decision-note" />
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(() => facilityAPI.decide(ticketId, 'approve', decisionNote), 'Freigegeben')} data-testid="facility-approve-btn">
                      <Check className="h-4 w-4 mr-1" /> Freigeben
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => act(() => facilityAPI.decide(ticketId, 'request_quote', decisionNote), 'Kostenvoranschlag angefordert')} data-testid="facility-request-quote-btn">
                      <FileQuestion className="h-4 w-4 mr-1" /> Angebot anfordern
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-destructive" disabled={busy} onClick={() => act(() => facilityAPI.decide(ticketId, 'reject', decisionNote), 'Abgelehnt')} data-testid="facility-reject-btn">
                      <X className="h-4 w-4 mr-1" /> Ablehnen
                    </Button>
                  </div>
                </div>
              )}

              {/* Kostenvoranschlag / Angebot */}
              {(canWork || canManage) && (
                <div className="rounded-xl border border-border/60 p-3 space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1"><Euro className="h-4 w-4" /> Kostenvoranschlag</Label>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" value={quoteAmount} onChange={(e) => setQuoteAmount(e.target.value)} placeholder="Betrag €" className="max-w-[140px]" data-testid="facility-quote-amount" />
                    <Input value={quoteNote} onChange={(e) => setQuoteNote(e.target.value)} placeholder="Notiz" data-testid="facility-quote-note" />
                    <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => act(() => facilityAPI.setQuote(ticketId, quoteAmount === '' ? null : parseFloat(quoteAmount), quoteNote), 'Angebot gespeichert')} data-testid="facility-save-quote-btn">Speichern</Button>
                  </div>
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); uploadKind(e.dataTransfer.files, 'quote'); }}
                    className="rounded-lg border-2 border-dashed border-border/60 p-2 text-center"
                    data-testid="facility-quote-dropzone"
                  >
                    <div className="flex flex-wrap gap-2 justify-center mb-1">
                      {quotePhotos.map((p) => <PhotoThumb key={p.id} photo={p} onDelete={(ph) => act(() => facilityAPI.deletePhoto(ph.id))} />)}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">Angebot (PDF/Bild) hierher ziehen oder:</p>
                    <div className="flex gap-2 justify-center">
                      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => quoteFileRef.current?.click()} data-testid="facility-quote-upload-btn">
                        <Upload className="h-4 w-4 mr-1" /> Datei
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => quoteCamRef.current?.click()} data-testid="facility-quote-camera-btn">
                        <Camera className="h-4 w-4 mr-1" /> Kamera
                      </Button>
                    </div>
                    <input ref={quoteFileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => uploadKind(e.target.files, 'quote')} />
                    <input ref={quoteCamRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadKind(e.target.files, 'quote')} />
                  </div>
                </div>
              )}

              {/* Zuweisung (Leitung) – mehrere Techniker möglich */}
              {canManage && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1"><Wrench className="h-4 w-4" /> Zuständige Haustechniker</Label>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(ticket.assigned_to || []).length ? (ticket.assigned_to || []).map((id, i) => (
                      <Badge key={id} variant="secondary" className="pr-1 gap-1" data-testid={`facility-assigned-${id}`}>
                        {ticket.assigned_names?.[i] || id}
                        <button
                          type="button"
                          className="ml-0.5 rounded-full hover:bg-background/60 p-0.5"
                          disabled={busy}
                          onClick={() => act(() => facilityAPI.assign(ticketId, (ticket.assigned_to || []).filter((x) => x !== id)), 'Zuweisung aktualisiert')}
                          data-testid={`facility-unassign-${id}`}
                          title="Entfernen"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )) : <span className="text-xs text-muted-foreground">Niemand zugewiesen</span>}
                  </div>
                  <Select value="" onValueChange={(v) => { if (!(ticket.assigned_to || []).includes(v)) act(() => facilityAPI.assign(ticketId, [...(ticket.assigned_to || []), v]), 'Zugewiesen'); }}>
                    <SelectTrigger data-testid="facility-assign-select"><SelectValue placeholder="Techniker hinzufügen…" /></SelectTrigger>
                    <SelectContent>
                      {technicians.filter((t) => !(ticket.assigned_to || []).includes(t.id)).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Terminierung + Arbeit (Techniker) */}
              {(canWork || canManage) && ['approved', 'in_progress', 'quote_requested'].includes(ticket.status) && (
                <div className="rounded-xl border border-border/60 p-3 space-y-3">
                  <Label className="text-sm font-medium flex items-center gap-1"><CalendarClock className="h-4 w-4" /> Termin</Label>
                  <div className="flex gap-2 items-end flex-wrap">
                    <div className="space-y-1">
                      <Label className="text-xs">Beginn</Label>
                      <DatePickerDE value={schedDate} onChange={setSchedDate} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Uhrzeit</Label>
                      <Input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} className="w-28" data-testid="facility-sched-time" />
                    </div>
                  </div>
                  <div className="flex gap-2 items-end flex-wrap">
                    <div className="space-y-1">
                      <Label className="text-xs">Ende (optional, für mehrtägige Aufgaben)</Label>
                      <DatePickerDE value={schedEndDate} onChange={setSchedEndDate} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Uhrzeit</Label>
                      <Input type="time" value={schedEndTime} onChange={(e) => setSchedEndTime(e.target.value)} className="w-28" data-testid="facility-sched-end-time" />
                    </div>
                    {schedEndDate && (
                      <Button type="button" variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => { setSchedEndDate(''); setSchedEndTime(''); }}>Ende entfernen</Button>
                    )}
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full w-fit" disabled={busy} onClick={saveSchedule} data-testid="facility-save-schedule-btn">Termin setzen</Button>
                  {ticket.scheduled_start && <p className="text-xs text-muted-foreground">Geplant: {fmtDateTimeDe(ticket.scheduled_start)}{ticket.scheduled_end ? ` – ${fmtDateTimeDe(ticket.scheduled_end)}` : ''}</p>}
                  {ticket.status === 'approved' && (
                    <Button size="sm" className="rounded-full" disabled={busy} onClick={() => act(() => facilityAPI.setStatus(ticketId, 'in_progress'), 'In Arbeit')} data-testid="facility-start-btn">
                      <Play className="h-4 w-4 mr-1" /> Arbeit beginnen
                    </Button>
                  )}
                </div>
              )}

              {/* Abschluss (Techniker) */}
              {(canWork || canManage) && ['in_progress', 'approved'].includes(ticket.status) && (
                <div className="rounded-xl border border-border/60 p-3 space-y-2 bg-emerald-50/50 dark:bg-emerald-950/20">
                  <Label className="text-sm font-medium flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Abschluss dokumentieren</Label>
                  <Textarea value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} placeholder="Was wurde gemacht?" rows={2} data-testid="facility-complete-note" />
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" value={materialCost} onChange={(e) => setMaterialCost(e.target.value)} placeholder="Materialkosten €" className="max-w-[160px]" data-testid="facility-material-cost" />
                    <Input type="number" value={workMinutes} onChange={(e) => setWorkMinutes(e.target.value)} placeholder="Arbeitszeit (Min.)" data-testid="facility-work-minutes" />
                  </div>
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); uploadKind(e.dataTransfer.files, 'completion'); }}
                    className="rounded-lg border-2 border-dashed border-border/60 p-2 text-center"
                    data-testid="facility-completion-dropzone"
                  >
                    <div className="flex flex-wrap gap-2 justify-center mb-1">
                      {completionPhotos.map((p) => <PhotoThumb key={p.id} photo={p} onDelete={(ph) => act(() => facilityAPI.deletePhoto(ph.id))} />)}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">Nachher-Fotos hierher ziehen oder:</p>
                    <div className="flex gap-2 justify-center">
                      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => afterFileRef.current?.click()} data-testid="facility-after-photo-btn">
                        <Upload className="h-4 w-4 mr-1" /> Datei
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => afterCamRef.current?.click()} data-testid="facility-after-camera-btn">
                        <Camera className="h-4 w-4 mr-1" /> Kamera
                      </Button>
                    </div>
                    <input ref={afterFileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadKind(e.target.files, 'completion')} />
                    <input ref={afterCamRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadKind(e.target.files, 'completion')} />
                  </div>
                  <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 w-full" disabled={busy} onClick={() => act(() => facilityAPI.complete(ticketId, { note: completeNote, material_cost: materialCost === '' ? null : parseFloat(materialCost), work_minutes: workMinutes === '' ? null : parseInt(workMinutes, 10) }), 'Als erledigt markiert')} data-testid="facility-complete-btn">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Als erledigt markieren
                  </Button>
                </div>
              )}

              {/* Abgeschlossen-Info */}
              {ticket.status === 'done' && ticket.completion && (
                <div className="rounded-xl border border-green-300/60 p-3 bg-green-50/60 dark:bg-green-950/20 text-sm space-y-1">
                  <p className="font-medium text-green-700 dark:text-green-300">Erledigt am {fmtDateTimeDe(ticket.completion.at)}</p>
                  {ticket.completion.note && <p>{ticket.completion.note}</p>}
                  {ticket.completion.material_cost != null && <p className="text-muted-foreground">Material: {ticket.completion.material_cost} €</p>}
                  {ticket.completion.work_minutes != null && <p className="text-muted-foreground">Arbeitszeit: {ticket.completion.work_minutes} Min.</p>}
                  {completionPhotos.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">{completionPhotos.map((p) => <PhotoThumb key={p.id} photo={p} />)}</div>
                  )}
                </div>
              )}

              <Separator />
              {/* Verlauf */}
              <div>
                <Label className="text-xs text-muted-foreground">Verlauf</Label>
                <ul className="mt-2 space-y-1.5" data-testid="facility-history">
                  {(ticket.history || []).slice().reverse().map((h) => (
                    <li key={h.id} className="text-xs flex gap-2">
                      <span className="text-muted-foreground whitespace-nowrap">{fmtDateTimeDe(h.at)}</span>
                      <span className="font-medium">{STATUS_LABELS[h.action] || h.action}</span>
                      {h.by_name && <span className="text-muted-foreground">· {h.by_name}</span>}
                      {h.note && <span className="text-muted-foreground truncate">– {h.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>

              {canManage && (
                <Button variant="ghost" size="sm" className="text-destructive" disabled={busy} onClick={handleDelete} data-testid="facility-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Meldung löschen
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
